import { PDFDocument, rgb } from 'pdf-lib'
import templateUrl from './template.pdf'
import { drawLogos } from "@/reports/shared_utils/drawLogos";
import { collectUnsupportedHighlights, drawUnsupportedHighlights } from "@/reports/shared_utils/highlightUnsupported";
import { sanitizeForPdf } from "@/reports/shared_utils/sanitizeForPdf";

// Maps each schema key to a representative PDF field name. Used by the live
// preview pane to find a widget rectangle for highlight/scroll targeting.
export const SCHEMA_TO_PDF_FIELD = {
  date: 'date',
  studentName: 'student',
  oen: 'oen',
  year: 'year1',
  daysAbsent: 'daysAbsent',
  totalDaysAbsent: 'totalDaysAbsent',
  timesLate: 'timesLate',
  totalTimesLate: 'totalTimesLate',
  frenchProgram: 'frenchImmersion',
  teacher: 'teacher',
  earlyChildhoodEducator: 'earlyChildhoodEducator',
  principal: 'principal',
  telephone: 'tel',
  board: 'board',
  school: 'school',
  schoolAddress: 'schoolAddress',
  boardAddress: 'boardAddress',
  placement: 'placementKY2',
  belongingEsl: 'belongingEsl',
  belongingIep: 'belongingIep',
  belongingComments: 'belongingComments',
  selfRegulationEsl: 'selfRegulationEsl',
  selfRegulationIep: 'selfRegulationIep',
  selfRegulationComments: 'selfRegulationComments',
  literacyMathEsl: 'literacyMathEsl',
  literacyMathIep: 'literacyMathIep',
  literacyMathComments: 'literacyMathComments',
  problemSolvingEsl: 'problemSolvingEsl',
  problemSolvingIep: 'problemSolvingIep',
  problemSolvingComments: 'problemSolvingComments',
  ersCompleted: 'ersCompletedYes',
  ersDateYear: 'ersDateYear',
  ersDateMonth: 'ersDateMonth',
  ersDateDay: 'ersDateDay',
  ersBenchmarkMet: 'ersBenchmarkYes',
  eceNames: 'eceNames',
}

export const PAGE_1_SCHEMA_FIELDS = new Set([
  'date', 'studentName', 'oen', 'year',
  'daysAbsent', 'totalDaysAbsent', 'timesLate', 'totalTimesLate', 'frenchProgram',
  'teacher', 'earlyChildhoodEducator', 'principal', 'telephone',
  'board', 'school', 'schoolAddress', 'boardAddress', 'placement',
  'belongingEsl', 'belongingIep', 'belongingComments',
  'selfRegulationEsl', 'selfRegulationIep', 'selfRegulationComments',
])

export const STEP_ANCHOR_FIELDS = {
  1: 'student',
  2: 'teacher',
  3: 'belongingComments',
  4: 'selfRegulationComments',
  5: 'literacyMathComments',
  6: 'problemSolvingComments',
  7: 'ersDateYear',
}

const val = (v) => sanitizeForPdf(v);
const isTrue = (v) => v === true || v === 'true' || v === 'on'

const fill = async (data, highlightRect = null, logos = []) => {
  const pdfBytes = await fetch(templateUrl).then((r) => r.arrayBuffer())
  const pdfDoc = await PDFDocument.load(pdfBytes)
  const form = pdfDoc.getForm()

  const text = (name, value, fontSize) => {
    const f = form.getTextField(name)
    f.setText(val(value))
    if (fontSize != null) f.setFontSize(fontSize)
  }
  const checkIf = (name, condition) => {
    const f = form.getCheckBox(name)
    condition ? f.check() : f.uncheck()
  }

  const studentName = data.studentName
  const oen = data.oen
  // Match the form's printed text: the identity labels ("Student:", "Teacher:", etc.) are
  // set at 8pt, so filled values use 8 to sit level with them and fit the 12pt cells. The
  // narrative comment boxes use 9 (the size of their printed placeholder hint). The template's
  // own field defaults are unreliable here (the comment boxes default to ~173pt), so every
  // field gets an explicit size. ERS date underscores stay at 8; checkboxes carry no size.
  const BODY = 8
  const COMMENT = 9

  // ----- Page 1 -----
  text('date', data.date, BODY)
  text('student', studentName, BODY)
  text('oen', oen, BODY)
  text('daysAbsent', data.daysAbsent, BODY)
  text('totalDaysAbsent', data.totalDaysAbsent, BODY)
  text('timesLate', data.timesLate, BODY)
  text('totalTimesLate', data.totalTimesLate, BODY)
  text('teacher', data.teacher, BODY)
  text('earlyChildhoodEducator', data.earlyChildhoodEducator, BODY)
  text('principal', data.principal, BODY)
  text('tel', data.telephone, BODY)
  text('board', data.board, BODY)
  text('school', data.school, BODY)
  text('schoolAddress', data.schoolAddress, BODY)
  form.getTextField('schoolAddress').enableMultiline()
  text('boardAddress', data.boardAddress, BODY)
  form.getTextField('boardAddress').enableMultiline()

  checkIf('year1', data.year === 'Year 1')
  checkIf('year2', data.year === 'Year 2')
  checkIf('frenchImmersion', data.frenchProgram === 'Immersion')
  checkIf('frenchCore',      data.frenchProgram === 'Core')
  checkIf('frenchExtended',  data.frenchProgram === 'Extended')
  checkIf('placementKY2', data.placement === 'Kindergarten Year 2')
  checkIf('placementG1',  data.placement === 'Grade 1')

  checkIf('belongingEsl', isTrue(data.belongingEsl))
  checkIf('belongingIep', isTrue(data.belongingIep))
  text('belongingComments', data.belongingComments, COMMENT)
  form.getTextField('belongingComments').enableMultiline()
  checkIf('selfRegulationEsl', isTrue(data.selfRegulationEsl))
  checkIf('selfRegulationIep', isTrue(data.selfRegulationIep))
  text('selfRegulationComments', data.selfRegulationComments, COMMENT)
  form.getTextField('selfRegulationComments').enableMultiline()

  // ----- Page 2 -----
  text('studentP2', studentName, BODY)
  text('oenP2', oen, BODY)
  checkIf('year1P2', data.year === 'Year 1')
  checkIf('year2P2', data.year === 'Year 2')

  checkIf('literacyMathEsl', isTrue(data.literacyMathEsl))
  checkIf('literacyMathIep', isTrue(data.literacyMathIep))
  text('literacyMathComments', data.literacyMathComments, COMMENT)
  form.getTextField('literacyMathComments').enableMultiline()
  checkIf('problemSolvingEsl', isTrue(data.problemSolvingEsl))
  checkIf('problemSolvingIep', isTrue(data.problemSolvingIep))
  text('problemSolvingComments', data.problemSolvingComments, COMMENT)
  form.getTextField('problemSolvingComments').enableMultiline()

  checkIf('ersCompletedYes', data.ersCompleted === 'yes')
  checkIf('ersCompletedNo',  data.ersCompleted === 'no')
  checkIf('ersCompletedNa',  data.ersCompleted === 'na')
  text('ersDateYear',  data.ersDateYear, 8)
  text('ersDateMonth', data.ersDateMonth, 8)
  text('ersDateDay',   data.ersDateDay, 8)
  checkIf('ersBenchmarkYes', data.ersBenchmarkMet === 'yes')
  checkIf('ersBenchmarkNo',  data.ersBenchmarkMet === 'no')

  text('eceNames', data.eceNames, BODY)

  // ----- Page 3 -----
  text('studentP3', studentName, BODY)
  text('oenP3', oen, BODY)
  checkIf('year1P3', data.year === 'Year 1')
  checkIf('year2P3', data.year === 'Year 2')

  text('studentTearOff', studentName, BODY)
  checkIf('year1TearOff', data.year === 'Year 1')
  checkIf('year2TearOff', data.year === 'Year 2')
  text('teacherTearOff', data.teacher, BODY)

  // ----- Page 4 -----
  text('studentP4', studentName, BODY)
  text('oenP4', oen, BODY)
  checkIf('year1P4', data.year === 'Year 1')
  checkIf('year2P4', data.year === 'Year 2')

  const __unsupportedHL = collectUnsupportedHighlights(pdfDoc, form);
  form.flatten()

  if (highlightRect) {
    const { pageIndex, x, y, width, height } = highlightRect
    const pages = pdfDoc.getPages()
    if (pages[pageIndex]) {
      pages[pageIndex].drawRectangle({
        x: x - 2, y: y - 2,
        width: width + 4, height: height + 4,
        color: rgb(0, 0.95, 0),
        opacity: 0.25,
        borderWidth: 0,
      })
    }
  }

  // Class logos: drawn last so they sit on top of the flattened template.
  drawUnsupportedHighlights(pdfDoc, __unsupportedHL);
  await drawLogos(pdfDoc, logos);

  const out = await pdfDoc.save()
  const blob = new Blob([out], { type: 'application/pdf' })
  return URL.createObjectURL(blob)
}

export { fill }
